import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Axios from 'axios';

import { Loading } from './Loading';
import { UserContext, SnackbarContext } from './userContext';
import { AccountHeader, EmptyBlock, Icon } from './myacc/primitives';

/* ─────────── helpers ─────────── */

function Line({ label, children }) {
  return (
    <div className="text-[12.5px] leading-[1.5]">
      <span className="text-fg-tertiary">{label}: </span>
      <span className="font-semibold text-fg-primary">{children}</span>
    </div>
  );
}

function ProductCard({ item }) {
  return (
    <div
      className="
        relative flex flex-col sm:flex-row gap-3 overflow-hidden
        rounded-xl border border-border-subtle bg-surface-paper
        transition-[border-color,transform] duration-200 ease-out-soft
        hover:border-amber/30 hover:-translate-y-0.5
      "
    >
      <div className="relative shrink-0 sm:w-[140px] aspect-[16/10] sm:aspect-auto bg-surface-overlay overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.product_name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-fg-muted text-[10px] font-bold tracking-wider">
            ESAY
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 p-3.5 sm:py-3 sm:pr-3.5 sm:pl-1">
        <h3 className="truncate text-[14px] font-bold text-fg-primary">{item.product_name}</h3>
        <div className="mt-0.5 text-[11.5px] font-bold uppercase tracking-[0.12em] text-amber">
          {item.sub_category_name}
        </div>
        {item.product_location_text && (
          <div className="mt-0.5 text-[11.5px] text-fg-tertiary inline-flex items-center gap-1">
            <Icon.Pin className="h-3 w-3" />
            <span className="truncate">{item.product_location_text}</span>
          </div>
        )}
        <div className="mt-2 space-y-0.5">
          <Line label="Mobile">{item.product_contact_mobile}</Line>
          {item.product_contact_email && <Line label="Email">{item.product_contact_email}</Line>}
          <Line label="Contacted">{item.contact_date}</Line>
        </div>
      </div>
    </div>
  );
}

function PersonCard({ item }) {
  return (
    <div
      className="
        rounded-xl border border-border-subtle bg-surface-paper p-4
        transition-[border-color,transform] duration-200 ease-out-soft
        hover:border-amber/30 hover:-translate-y-0.5
      "
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 flex h-11 w-11 items-center justify-center rounded-xl border border-amber/40 bg-gradient-to-br from-amber/30 to-amber/8 text-amber font-bold text-[16px]">
          {(item.user_full_name || '?').trim().charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[14px] font-bold text-fg-primary">
            {item.user_full_name} {item.user_last_name}
          </h3>
          <div className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-amber">
            {item.sub_category_name}
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-0.5 border-t border-border-subtle pt-3">
        <Line label="Mobile">{item.user_mobile}</Line>
        {item.user_email && <Line label="Email">{item.user_email}</Line>}
        <Line label="Contacted">{item.contact_date}</Line>
      </div>
    </div>
  );
}

/* ─────────── Tab ─────────── */

function TabBtn({ active, count, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative inline-flex items-center gap-2 px-3 sm:px-4 h-11 shrink-0
        text-[12px] font-bold uppercase tracking-[0.1em]
        transition-colors duration-200 ease-out-soft
        ${active ? 'text-amber' : 'text-fg-tertiary hover:text-fg-secondary'}
      `}
    >
      <span>{label}</span>
      <span className={`
        inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5
        text-[10px] font-bold
        ${active ? 'bg-amber text-ink' : 'bg-surface-overlay text-fg-secondary'}
      `}>
        {count}
      </span>
      {active && (
        <span aria-hidden className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-amber" />
      )}
    </button>
  );
}

/* ─────────── component ─────────── */

export const MyAccContactHistory = () => {
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [prods, setProds] = useState([]);
  const [engs, setEngs] = useState([]);
  const [wfs, setWfs] = useState([]);

  useEffect(() => {
    Axios.post('/new_apis/getcontacthistory',
      { uid: user.user_id },
      { headers: { tkn: user.token } },
    ).then((res) => {
      const s = res.data.status;
      if (s === 1) {
        setProds(res.data.prodserv || []);
        setEngs(res.data.eng || []);
        setWfs(res.data.wf || []);
        setLoading(false);
      } else if (s === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else if (s === 4) {
        setUser(null);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      }
    });
  }, []); // eslint-disable-line

  if (loading) return <Loading />;

  return (
    <>
      <AccountHeader
        kicker="Activity"
        title="Contact history"
        subtitle="People and listings you've reached out to through ESAY."
      />

      <section className="rounded-2xl border border-border-subtle bg-surface-paper overflow-hidden">
        {/* Tab strip */}
        <div className="border-b border-border-subtle overflow-x-auto scrollbar-hidden">
          <div className="flex">
            <TabBtn active={tab === 0} count={prods.length} label="Suppliers"  onClick={() => setTab(0)} />
            <TabBtn active={tab === 1} count={engs.length}  label="Engineers"  onClick={() => setTab(1)} />
            <TabBtn active={tab === 2} count={wfs.length}   label="Workforce"  onClick={() => setTab(2)} />
          </div>
        </div>

        <div className="p-3 sm:p-4">
          {tab === 0 && (
            prods.length === 0
              ? <EmptyBlock icon={<Icon.History className="h-7 w-7" />} title="No supplier contacts yet" subtitle="You haven't contacted any supplier or service provider." />
              : (
                <div className="grid grid-cols-1 gap-3">
                  {prods.map((item, i) => <ProductCard key={i} item={item} />)}
                </div>
              )
          )}

          {tab === 1 && (
            engs.length === 0
              ? <EmptyBlock icon={<Icon.History className="h-7 w-7" />} title="No engineers contacted" subtitle="You haven't contacted any engineer yet." />
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {engs.map((item, i) => <PersonCard key={i} item={item} />)}
                </div>
              )
          )}

          {tab === 2 && (
            wfs.length === 0
              ? <EmptyBlock icon={<Icon.History className="h-7 w-7" />} title="No workforce contacted" subtitle="You haven't contacted any workforce yet." />
              : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {wfs.map((item, i) => <PersonCard key={i} item={item} />)}
                </div>
              )
          )}
        </div>
      </section>
    </>
  );
};
